/* eslint-disable @typescript-eslint/no-floating-promises */
import { MaterialTableProps } from "@material-table/core";
import {
  Box,
  Button,
  TablePagination,
  TablePaginationProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  PortCallCombinedDto,
  PortDetailsDto,
  usePortApi,
  useThirdPartyApi,
} from "~/api/GeneratedApi";
import GenericTable, { CustomColumn } from "~/components/GenericTable";
import { CustomQueryKey } from "~/utils/hooks/QueryKeys";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { zoomOnFeature } from "../../functions/zoomOnFeature";
import { BerthOrderDialog } from "./BerthOrderDialog";
import { PortCallCommentForm } from "./PortCallCommentForm";

type Props = {
  port: PortDetailsDto;
  containerHeight: number;
  map?: Map;
  closeDrawer: () => void;
};

const InfoLabel = ({
  title,
  value,
  rightSide = false,
}: {
  title: string;
  value: string | { text: string; tooltip: string }[];
  rightSide?: boolean;
}) => (
  <>
    <td>
      <Typography
        component="span"
        variant="subtitle1"
        sx={{ ml: rightSide ? 2 : 0 }}
      >
        {title}
      </Typography>
    </td>
    <td style={{ paddingLeft: "20px" }}>
      {typeof value === "string" ? (
        <Typography component="span" variant="body2">
          {value}
        </Typography>
      ) : (
        value.map((v) => (
          <Tooltip title={v.tooltip ?? ""} key={`${v.text}${v.tooltip}`}>
            <Typography component="p" variant="body2">
              {v.text}
            </Typography>
          </Tooltip>
        ))
      )}
    </td>
  </>
);

const MapPortCallsDrawerContent = ({
  port,
  containerHeight,
  map,
  closeDrawer,
}: Props): JSX.Element => {
  const dateFormatter = useDateFormatter();
  const { getAISItems, getPortCallsCombined } = useThirdPartyApi();
  const { getPortCallComments } = usePortApi();

  const { data: vessels } = useCustomQuery(
    "digitraffic-data",
    () => getAISItems(),
    {
      refetchOnWindowFocus: false,
    },
  );

  const portCallCommentQueryKey: CustomQueryKey = React.useMemo(
    () => ({ key: "port-call-comments", locode: port.locode.locode! }),
    [port.locode.locode],
  );

  const { data: portCallComments } = useCustomQuery(
    portCallCommentQueryKey,
    () => getPortCallComments(port.locode.locode!),
    {
      enabled: !!port.locode.locode,
      select: (d) => d.results,
    },
  );

  const { t } = useTranslation("port-calls");
  const { show } = usePopup();
  const theme = useTheme();

  const renderTableTimeColumn = React.useCallback(
    (data: PortCallCombinedDto, type: "arrival" | "departure"): JSX.Element => {
      let isEstimated = false;
      const latestActual = getLatestPortCallTime(
        type === "arrival" ? "ata" : "atd",
        data,
      );
      const latestEstimate = getLatestPortCallTime(
        type === "arrival" ? "eta" : "etd",
        data,
      );
      let date: Date | undefined = latestActual?.time;
      if (!date) {
        date = latestEstimate?.time;
        isEstimated = true;
      }

      return date ? (
        <Tooltip
          title={
            isEstimated
              ? t("estimated-time", {
                  source: latestEstimate?.source ?? "",
                }).toString()
              : t("actual-time", {
                  source: latestActual?.source ?? "",
                }).toString()
          }
        >
          <span>{`${isEstimated ? "~ " : ""}${dateFormatter({
            date,
          })}`}</span>
        </Tooltip>
      ) : (
        t("no-date-available")
      );
    },
    [dateFormatter, t],
  );

  const renderArrivalColumn = React.useCallback(
    (data: PortCallCombinedDto) => renderTableTimeColumn(data, "arrival"),
    [renderTableTimeColumn],
  );
  const renderDepartureColumn = React.useCallback(
    (data: PortCallCombinedDto) => renderTableTimeColumn(data, "departure"),
    [renderTableTimeColumn],
  );
  const renderUpdateColumn = React.useCallback(
    (data: PortCallCombinedDto): JSX.Element => {
      const latestCallUpdate = getLatestPortCallUpdate(data);
      const date = latestCallUpdate?.updateTime;

      return date ? (
        <Tooltip
          title={t("actual-time", {
            source: latestCallUpdate?.source ?? "",
          }).toString()}
        >
          <span>{`${dateFormatter({
            date,
          })}`}</span>
        </Tooltip>
      ) : (
        t("no-date-available")
      );
    },
    [dateFormatter, t],
  );

  const timeColumnSort = React.useCallback(
    (
      a: PortCallCombinedDto,
      b: PortCallCombinedDto,
      type: "arrival" | "departure",
    ): number => {
      const actual = type === "arrival" ? "ata" : "atd";
      const estimated = type === "arrival" ? "eta" : "etd";
      const date1: Date | undefined =
        getLatestPortCallTime(actual, a)?.time ??
        getLatestPortCallTime(estimated, a)?.time;

      const date2: Date | undefined =
        getLatestPortCallTime(actual, b)?.time ??
        getLatestPortCallTime(estimated, b)?.time;
      return (date1?.getTime() ?? 0) - (date2?.getTime() ?? 0);
    },
    [],
  );

  const arrivalColumnSort = React.useCallback(
    (a: PortCallCombinedDto, b: PortCallCombinedDto) =>
      timeColumnSort(a, b, "arrival"),
    [timeColumnSort],
  );
  const departureColumnSort = React.useCallback(
    (a: PortCallCombinedDto, b: PortCallCombinedDto) =>
      timeColumnSort(a, b, "departure"),
    [timeColumnSort],
  );
  const updateColumnSort = React.useCallback(
    (a: PortCallCombinedDto, b: PortCallCombinedDto) => {
      const date1 = getLatestPortCallUpdate(a)?.updateTime;
      const date2 = getLatestPortCallUpdate(b)?.updateTime;
      return (date1?.getTime() ?? 0) - (date2?.getTime() ?? 0);
    },
    [],
  );

  const columns = React.useRef<CustomColumn<PortCallCombinedDto>[]>([
    {
      title: t("vessel-name"),
      field: "vesselName",
    },
    {
      title: t("from"),
      field: "prevPort",
    },
    {
      title: t("to"),
      field: "portToVisit",
    },
    {
      title: t("arrival-time-header"),
      customSort: arrivalColumnSort,
      render: renderArrivalColumn,
    },
    {
      title: t("departure-time-header"),
      customSort: departureColumnSort,
      render: renderDepartureColumn,
    },
    {
      title: t("update-time"),
      defaultSort: "desc",
      customSort: updateColumnSort,
      render: renderUpdateColumn,
    },
  ]);

  const { data: portCalls } = useCustomQuery(
    "port-calls",
    () =>
      getPortCallsCombined(port.locode.locode!, {
        startTime: new Date(Date.now() - 86400000 * 14),
      }),
    {
      refetchOnWindowFocus: false,
      select: (data) =>
        data.filter(
          (x) =>
            !port.locode.portAreaCode ||
            !x.portAreaCode ||
            x.portAreaCode === port.locode.portAreaCode,
        ),
    },
  );

  const tableHeight = containerHeight - 53 - 48;

  const generateInfoLabel = React.useCallback(
    (
      data: PortCallCombinedDto,
      type: PortCallTimestampType | AdditionalPortCallTimestampType,
      rightSide?: boolean,
    ): JSX.Element => {
      const calls = getAllPortCallsForType(data, type);
      return (
        <InfoLabel
          title={t(type)}
          rightSide={rightSide}
          value={
            calls?.length > 0
              ? calls?.map((c) => ({
                  text: `${c.source}: ${dateFormatter({
                    date: c.time,
                  })}`,
                  tooltip: t("port-call-updated", {
                    updateTime: dateFormatter({
                      date: c.updateTime,
                    }),
                  }),
                }))
              : ""
          }
        />
      );
    },
    [dateFormatter, t],
  );

  const detailPanel = React.useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ rowData }: { rowData: PortCallCombinedDto }) => {
      const {
        prevPort,
        portToVisit,
        discharge,
        notLoading,
        metadataMmsi,
        portAreaName,
        berthName,
      } = rowData;
      const vessel = vessels?.find((x) => x.mmsi === metadataMmsi);
      const ata = getLatestPortCallTime("ata", rowData)?.time;
      const atd = getLatestPortCallTime("atd", rowData)?.time;
      const eta = getLatestPortCallTime("eta", rowData)?.time;
      const etd = getLatestPortCallTime("etd", rowData)?.time;

      return (
        <Box m={1} ml={2} display="flex">
          <Box>
            <table>
              <tbody>
                <tr>
                  <InfoLabel
                    title={t("previous-port")}
                    value={prevPort ?? ""}
                  />
                  <InfoLabel
                    title={t("next-port")}
                    value={portToVisit ?? ""}
                    rightSide
                  />
                </tr>
                <tr>
                  {generateInfoLabel(rowData, "ata")}
                  {generateInfoLabel(rowData, "atd", true)}
                </tr>
                <tr>
                  {generateInfoLabel(rowData, "eta")}
                  {generateInfoLabel(rowData, "etd", true)}
                </tr>
                <tr>
                  {generateInfoLabel(rowData, "rps")}
                  {generateInfoLabel(rowData, "aps", true)}
                </tr>
                <tr>
                  {generateInfoLabel(rowData, "pps")}
                  {generateInfoLabel(rowData, "apc", true)}
                </tr>
                <tr>
                  <InfoLabel
                    title={t("port-area")}
                    value={portAreaName ?? ""}
                  />
                  <InfoLabel
                    title={t("unloading")}
                    value={discharge ? t("general:yes") : t("general:no") ?? ""}
                    rightSide
                  />
                </tr>
                <tr>
                  <InfoLabel title={t("berth")} value={berthName ?? ""} />
                  <InfoLabel
                    title={t("loading")}
                    value={
                      !notLoading ? t("general:yes") : t("general:no") ?? ""
                    }
                    rightSide
                  />
                </tr>
              </tbody>
            </table>
          </Box>
          <Box ml={2}>
            <Box>
              <Button
                disabled={!vessel}
                variant="outlined"
                onClick={() => {
                  if (!vessel) return;
                  if (!map) return;
                  if (!zoomOnFeature(map, `vessel${vessel.mmsi}`, 14)) return;
                  closeDrawer();
                }}
              >
                {t("go-to-ship")}
              </Button>
              <Button
                disabled={!vessel}
                variant="outlined"
                sx={{ ml: theme.spacing(1) }}
                onClick={() => {
                  if (!vessel) return;
                  const startDate = ata ?? eta;
                  const endDate = atd ?? etd;
                  const roundTo = 1000 * 60 * 30; // 30 minutes in ms
                  show(
                    ({ close }) => (
                      <BerthOrderDialog
                        open
                        activeAction="add"
                        onClose={close}
                        portId={port.id}
                        berthOrderId={undefined}
                        berthName={berthName}
                        // Start is rounded down, end is rounded up
                        startTimestamp={
                          startDate
                            ? new Date(
                                Math.floor(startDate.valueOf() / roundTo) *
                                  roundTo,
                              )
                            : undefined
                        }
                        endTimestamp={
                          endDate
                            ? new Date(
                                Math.ceil(endDate.valueOf() / roundTo) *
                                  roundTo,
                              )
                            : undefined
                        }
                        mmsi={metadataMmsi}
                      />
                    ),
                    { noDialog: true },
                  );
                }}
              >
                {t("berths:add-order")}
              </Button>
            </Box>
            <Box mt={3}>
              <PortCallCommentForm
                portId={port.id}
                mapAreaIds={port.mapAreas?.map((x) => x.mapAreaId) ?? []}
                queryKey={portCallCommentQueryKey}
                portCallId={rowData.portCallId}
                existingPortCalls={portCallComments}
              />
            </Box>
          </Box>
        </Box>
      );
    },
    [
      closeDrawer,
      generateInfoLabel,
      map,
      port.id,
      port.mapAreas,
      portCallCommentQueryKey,
      portCallComments,
      show,
      t,
      theme,
      vessels,
    ],
  );

  const data = React.useMemo(() => portCalls ?? [], [portCalls]);

  const tableProps: Partial<MaterialTableProps<PortCallCombinedDto>> =
    React.useMemo(
      () => ({
        title: t("title", {
          port: port.name,
          locode: port.locode.locode,
          area: port.locode.portAreaCode,
        }),
        options: {
          maxBodyHeight: `${tableHeight}px`,
          minBodyHeight: `${tableHeight}px`,
          pageSizeOptions: [5, 10, 15, 25, 50],
          pageSize: 10,
        },
        components: {
          Container: (props) => (
            <div
              {...props}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                border: "none",
                boxShadow: "none",
              }}
            />
          ),
          Pagination: (props: TablePaginationProps) => {
            const { style } = props;
            return (
              <TablePagination
                {...props}
                style={{
                  borderBottomRightRadius: theme.shape.borderRadius,
                  ...style,
                }}
              />
            );
          },
        },
        detailPanel,
      }),
      [
        detailPanel,
        port.locode.locode,
        port.locode.portAreaCode,
        port.name,
        t,
        tableHeight,
        theme.shape.borderRadius,
      ],
    );

  return (
    <GenericTable<PortCallCombinedDto>
      columns={columns.current}
      data={data}
      tableProps={tableProps}
    />
  );
};

export default MapPortCallsDrawerContent;

type PortCallTime = {
  time: Date;
  updateTime: Date;
  source: string;
};

const PortCallTimeStampTypes = ["ata", "eta", "atd", "etd"] as const;
type PortCallTimestampType = typeof PortCallTimeStampTypes[number];
const AdditionalPortCallTimestampTypes = [
  "apc",
  "aps",
  "ata",
  "atd",
  "eta",
  "etd",
  "pps",
  "rps",
] as const;
type AdditionalPortCallTimestampType =
  typeof AdditionalPortCallTimestampTypes[number];
// const PortCallSources = [
//   "port",
//   "agent",
//   "awake.ai",
//   "portnet",
//   "pilotweb",
// ] as const;
// type PortCallSource = typeof PortCallSources[number];

function getLatestPortCallTime(
  type: PortCallTimestampType,
  data: PortCallCombinedDto,
): PortCallTime | null {
  const additionals = data.additionalCalls;
  const originalTime = data[type];
  const updateTimeKey = `${type}Timestamp` as keyof PortCallCombinedDto;
  const sourceKey = `${type}Source` as keyof PortCallCombinedDto;
  const originalUpdateTime = data[updateTimeKey] as Date | undefined;
  const originalSource = data[sourceKey] as string | undefined;

  // Sort the additional timeCalls and find the newest
  const newestAdditional = additionals
    ?.filter?.((c) => c.eventType?.toLowerCase() === type)
    ?.sort(
      (a, b) =>
        new Date(b.recordTime).getTime() - new Date(a.recordTime).getTime(),
    )?.[0];

  // If only original ata exists, return that, or null if both are missing
  if (
    !newestAdditional ||
    !newestAdditional.eventTime ||
    !newestAdditional.recordTime
  ) {
    if (originalTime && originalUpdateTime) {
      return {
        time: originalTime,
        updateTime: originalUpdateTime,
        source: originalSource ?? "",
      };
    }
    return null;
  }

  // If only new ata exists, return that, or null if both are missing
  if (!originalTime || !originalUpdateTime) {
    if (
      newestAdditional &&
      newestAdditional.eventTime &&
      newestAdditional.recordTime
    ) {
      return {
        time: newestAdditional.eventTime,
        updateTime: newestAdditional.recordTime,
        source: newestAdditional.source ?? "",
      };
    }
    return null;
  }

  // Return the one that has been updated more recently
  return originalUpdateTime > newestAdditional.recordTime
    ? {
        time: originalTime,
        updateTime: originalUpdateTime,
        source: originalSource ?? "",
      }
    : {
        time: newestAdditional.eventTime,
        updateTime: newestAdditional.recordTime,
        source: newestAdditional.source ?? "",
      };
}

function getAllPortCallsForType(
  data: PortCallCombinedDto,
  type: PortCallTimestampType | AdditionalPortCallTimestampType,
): PortCallTime[] {
  const results: PortCallTime[] = [];
  if (PortCallTimeStampTypes.includes(type as PortCallTimestampType)) {
    const typedType = type as PortCallTimestampType;
    const time = data[typedType];

    const updateTimeKey = `${typedType}Timestamp` as keyof PortCallCombinedDto;
    const sourceKey = `${typedType}Source` as keyof PortCallCombinedDto;
    const updateTime = data[updateTimeKey] as Date | undefined;
    const source = data[sourceKey] as string | undefined;

    if (time && updateTime)
      results.push({ time, updateTime, source: source ?? "" });
  }
  data.additionalCalls
    ?.filter?.((c) => c.eventType?.toLowerCase() === type)
    ?.forEach((c) => {
      const time = c.eventTime;
      const updateTime = c.recordTime;
      const source = c.source ?? "";
      if (time && updateTime) results.push({ time, updateTime, source });
    });

  return results;
}

function getLatestPortCallUpdate(
  data: PortCallCombinedDto,
): PortCallTime | null {
  let latestOriginal: PortCallTime | null = null;
  let latestAdditional: PortCallTime | null = null;
  PortCallTimeStampTypes.forEach((x) => {
    const time = data[x];

    const updateTimeKey = `${x}Timestamp` as keyof PortCallCombinedDto;
    const sourceKey = `${x}Source` as keyof PortCallCombinedDto;
    const updateTime = data[updateTimeKey] as Date | undefined;
    const source = data[sourceKey] as string | undefined;
    if (
      time &&
      updateTime &&
      (!latestOriginal || updateTime > latestOriginal.updateTime)
    )
      latestOriginal = { time, updateTime, source: source ?? "" };
  });
  data.additionalCalls?.forEach((x) => {
    const time = x.eventTime;
    const updateTime = x.recordTime;
    const source = x.source ?? "";
    if (
      time &&
      updateTime &&
      (!latestAdditional || updateTime > latestAdditional.updateTime)
    )
      latestAdditional = { time, updateTime, source };
  });
  if (!latestOriginal && !latestAdditional) return null;
  if (!latestOriginal && latestAdditional) return latestAdditional;
  if (latestOriginal && !latestAdditional) return latestOriginal;
  return latestOriginal!.updateTime > latestAdditional!.updateTime
    ? latestOriginal
    : latestAdditional;
}
